import { config } from '../config/env';
import logger from './logger';
import { redirectToLogin } from '../api/auth-handler';
import { refreshAccessToken } from '../api/tokenRefresh';

/**
 * Setup global fetch interceptor to handle 401 errors
 * This overrides the native fetch to add authentication error handling
 */
export function setupGlobalFetchInterceptor() {
  // Store the original fetch
  const originalFetch = window.fetch;

  // Override the global fetch
  window.fetch = async function(...args) {
    const [url, options = {}] = args;

    try {
      // Call the original fetch
      let response = await originalFetch(url, options);

      // Check if this is an API call (not external resources)
      const isApiCall =
        url.toString().includes('/api/') ||
        url.toString().includes(config.BACKEND_URL) ||
        url.toString().includes(config.COMMON_AUTH_URL);

      // Check for 401 on API calls
      if (isApiCall && response.status === 401) {
        // JWT_EXPIRED = the access JWT lapsed but the session is still valid.
        // Silently re-mint and retry the request once (covers raw-fetch callers
        // like CommonAuthContext). The refresh uses axios, not fetch, so it does
        // not recurse through this interceptor.
        if (!options._retry && response.headers.get('x-error-code') === 'JWT_EXPIRED') {
          options._retry = true;
          if (await refreshAccessToken()) {
            response = await originalFetch(url, options);
            if (response.status !== 401) return response;
          }
        }

        // Special handling for auth validation endpoint - don't redirect from here
        const isAuthValidation = url.toString().includes('/auth/validate');
        const isAuthCsrf = url.toString().includes('/auth/csrf');
        const isAuthLogout = url.toString().includes('/auth/logout');

        if (!isAuthValidation && !isAuthCsrf && !isAuthLogout) {
          logger.warn('🔒 401 Unauthorized detected on API call - session expired or invalid');
          logger.debug('🔒 Unauthorized URL:', url);

          // Centralized: clears session + redirects to Common Auth with
          // product=athena&redirect=<path>. Small delay lets pending ops finish.
          setTimeout(() => redirectToLogin(), 100);
        }
      }

      return response;
    } catch (error) {
      // Re-throw any errors from the original fetch
      throw error;
    }
  };

  logger.debug('✅ Global fetch interceptor installed');
}

/**
 * Setup all global interceptors
 */
export function setupGlobalInterceptors() {
  setupGlobalFetchInterceptor();
}